import * as React from 'react'
import * as events from '../../../events'
import { connect } from 'react-redux'
import Button from '@doordash/component-button'
import Tag from '@doordash/component-tag'
import {
  Colors,
  Inset,
  StackChildren,
  Text,
  Inline,
} from '@doordash/design-language'
import { CheckrStatus, ThinFileStatus } from '../../../models/types'
import { AlertWrapper, StepButtonContainer } from './styles'
import {
  CheckrStatusResults,
  BACKGROUND_CHECK_HELP_URL,
  CHECKR_APPLICANT_URL,
} from '../../../constants'
import { background, backgroundCheckStatus } from './strings'

type BackgroundStepProps = {
  email: string
  isComplete: boolean
  backgroundCheckCompletionUrl?: string
  trackConfirmationPageClick?: (string, object?) => Promise<any>
  thinFileStatus?: ThinFileStatus
  backgroundCheckResults?: CheckrStatusResults
  mvReportResults?: CheckrStatusResults
}

const generateCheckrCTAText = (status: CheckrStatus) => {
  if (status === CheckrStatus.Clear) {
    return null
  } else if (
    status === CheckrStatus.Waiting ||
    status === CheckrStatus.Delayed
  ) {
    return backgroundCheckStatus.inProgress.action()
  }
  return backgroundCheckStatus.needsAction.action()
}

const getCheckrStatusCategory = (bgc, mvr) => {
  if (
    bgc === CheckrStatusResults.Suspended ||
    mvr === CheckrStatusResults.Suspended
  ) {
    return CheckrStatus.NeedsAttention
  }

  if (bgc === CheckrStatusResults.Clear) {
    if (
      mvr === CheckrStatusResults.Clear ||
      mvr === CheckrStatusResults.Skipped
    ) {
      return CheckrStatus.Clear
    }
  }

  return CheckrStatus.Delayed
}

const BackgroundCheckDetailedStep: React.SFC<BackgroundStepProps> = ({
  isComplete,
  trackConfirmationPageClick,
  backgroundCheckCompletionUrl,
  thinFileStatus,
  backgroundCheckResults,
  mvReportResults,
}) => {
  const status = getCheckrStatusCategory(
    backgroundCheckResults,
    mvReportResults
  )

  const clearThinFile = thinFileStatus === ThinFileStatus.Clear

  // We only hide CTA on clear and consider cases
  const shouldShowCTA =
    status !== CheckrStatus.Clear &&
    backgroundCheckResults !== CheckrStatusResults.Consider &&
    mvReportResults !== CheckrStatusResults.Consider

  // Thinfile status supsersedes all Checkr Statuses
  if (!clearThinFile) {
    return (
      <StackChildren size={StackChildren.Sizes.XxSmall}>
        <AlertWrapper>
          <Inset size={Inset.Sizes.XxSmall}>
            <Text weight={Text.Weights.Bold}>{background.alertTitle()}</Text>
            <Text>{background.thinFileAlert()}</Text>
          </Inset>
        </AlertWrapper>
      </StackChildren>
    )
  }

  return (
    <StackChildren size={StackChildren.Sizes.XxSmall}>
      {isComplete || status === CheckrStatus.Clear ? (
        <React.Fragment>
          <Tag
            size={Tag.Sizes.Medium}
            text={backgroundCheckStatus.complete.label()}
            type={Tag.Types.HighlightDefault}
          />
          <Text styles={Text.Styles.SectionSubtext} color={Colors.SystemGrey80}>
            {backgroundCheckStatus.complete.description()}
          </Text>
        </React.Fragment>
      ) : status === CheckrStatus.NeedsAttention ? (
        <React.Fragment>
          <Tag
            size={Tag.Sizes.Medium}
            text={backgroundCheckStatus.needsAction.label()}
            type={Tag.Types.WarningEmphasis}
          />
          <Text color={Colors.SystemGrey80}>
            {backgroundCheckStatus.needsAction.description(
              backgroundCheckResults,
              mvReportResults
            )}
          </Text>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Tag
            size={Tag.Sizes.Medium}
            text={backgroundCheckStatus.inProgress.label()}
          />
          <Text color={Colors.SystemGrey80}>
            {backgroundCheckStatus.inProgress.description(
              backgroundCheckResults,
              mvReportResults
            )}
          </Text>
        </React.Fragment>
      )}
      <StepButtonContainer>
        <div>
          {shouldShowCTA && (
            <Inline size={Inline.Sizes.XxSmall}>
              <Button
                type={Button.Types.PrimaryPill}
                isInline
                size={Button.Sizes.Small}
                href={backgroundCheckCompletionUrl || CHECKR_APPLICANT_URL}
                tag="a"
                target={'_blank'}
                onClick={e => {
                  trackConfirmationPageClick('background_check_status_button', {
                    button: 'check_status',
                  })
                  e.stopPropagation()
                }}
              >
                {generateCheckrCTAText(status)}
              </Button>
            </Inline>
          )}
          {status !== CheckrStatus.Clear && (
            <Button
              type={Button.Types.TertiaryPill}
              isInline
              size={Button.Sizes.Small}
              tag="a"
              target={'_blank'}
              onClick={e => {
                trackConfirmationPageClick('background_check_status_button', {
                  button: 'help',
                })
                e.stopPropagation()
              }}
              href={BACKGROUND_CHECK_HELP_URL}
            >
              {backgroundCheckStatus.help()}
            </Button>
          )}
        </div>
      </StepButtonContainer>
    </StackChildren>
  )
}
const mapDispatchToProps = {
  trackConfirmationPageClick: events.trackConfirmationPageClick,
}
export default connect(null, mapDispatchToProps)(BackgroundCheckDetailedStep)
